import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  $isRootNode,
  COMMAND_PRIORITY_HIGH,
  KEY_TAB_COMMAND,
} from 'lexical';
import { useEffect } from 'react';

/**
 * Prevents Lexical error #4 when a user presses Tab on a root selection.
 *
 * A root selection is valid around top-level tables and decorator nodes, after
 * some whole-document selections, and while an editor is empty. However,
 * Lexical 0.39's TabIndentationPlugin assumes that every range-selection point
 * has a block ancestor. It calls `$getNearestBlockElementAncestorOrThrow` with
 * the root node and throws. The same unguarded call remains in Lexical's current
 * implementation:
 * https://github.com/facebook/lexical/blob/main/packages/lexical-extension/src/TabIndentationExtension.ts
 *
 * Handle only the unsupported root case before TabIndentationPlugin runs. Do
 * not prevent the native event, so Tab moves focus out of the editor. All other
 * selections continue to Lexical's standard indentation handler.
 */
export function RootSelectionTabPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_TAB_COMMAND,
      () => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;

        return [selection.anchor, selection.focus].some((point) =>
          $isRootNode(point.getNode())
        );
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);

  return null;
}
